import { useState } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { TransactionFlow } from '../../enums';
import { useLazyGetBankSlipReceiptV2Query, useLazyGetChargeBilletReceiptQuery, useLazyGetPaidBankSlipReceiptQuery, useLazyGetPaidBilletReceiptQuery, useLazyGetScheduleBilletReceiptQuery, } from '../../services/receiptApi';
import { mapBilletReceiptFlow } from '../../utils/mapBilletReceiptFlow';
export function useGetBilletReceipt() {
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBaasV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const [triggerPaidBankSlip] = useLazyGetPaidBankSlipReceiptQuery();
    const [triggerBankSlipV2] = useLazyGetBankSlipReceiptV2Query();
    const [triggerPaidBillet] = useLazyGetPaidBilletReceiptQuery();
    const [triggerChargeBillet] = useLazyGetChargeBilletReceiptQuery();
    const [triggerScheduleBillet] = useLazyGetScheduleBilletReceiptQuery();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [billetReceipt, setBilletReceipt] = useState();
    async function getBankSlipReceiptV2(transactionId, flow) {
        const { data } = await triggerBankSlipV2({ id: transactionId, flow: mapBilletReceiptFlow(flow) ?? '' }, false);
        if (!data?.result) {
            setError(true);
            return;
        }
        setBilletReceipt({
            flow,
            id: data.result.id,
            amount: data.result.amount,
            fees: data.result.fees,
            fine: data.result.fine,
            discounts: data.result.discount,
            dueDate: data.result.dueDate,
            transactionDate: data.result.dateTransaction,
            receiverName: data.result.receiver,
            receiverIdentificationNumber: data.result.documentNumber,
            barcode: data.result.barCode,
        });
    }
    async function getPaidBilletReceipt(transactionId, coreBankId, instructionFlow) {
        if (isBaasV2Enabled) {
            await getBankSlipReceiptV2(transactionId, TransactionFlow.CASH_OUT);
            return;
        }
        const { data } = instructionFlow
            ? await triggerPaidBankSlip({ id: transactionId }, false)
            : await triggerPaidBillet({ transactionId, coreBankId }, false);
        if (!data?.result) {
            setError(true);
        }
        else {
            setBilletReceipt({
                flow: TransactionFlow.CASH_OUT,
                id: data.result.id,
                amount: data.result.amount,
                charges: data.result.charges,
                discounts: data.result.discount,
                dueDate: data.result.dueDate,
                transactionDate: data.result.dateTransaction,
                receiverName: data.result.receiver,
                receiverIdentificationNumber: data.result.documentNumber,
                barcode: data.result.barCode,
            });
        }
    }
    async function getChargeBilletReceipt(transactionId, coreBankId) {
        if (isBaasV2Enabled) {
            await getBankSlipReceiptV2(transactionId, TransactionFlow.CASH_IN);
            return;
        }
        const { data } = await triggerChargeBillet({ transactionId, coreBankId }, false);
        if (!data?.result) {
            setError(true);
        }
        else {
            setBilletReceipt({
                flow: TransactionFlow.CASH_IN,
                id: data.result.id,
                amount: data.result.amount,
                barcode: data.result.barCode,
                discounts: data.result.discount,
                fees: data.result.interest,
                fine: data.result.fine,
                dueDate: data.result.dueDate,
                transactionDate: data.result.transactionAt,
                receiverName: data.result.drawee?.name,
                receiverIdentificationNumber: data.result.drawee?.taxIdentifier,
            });
        }
    }
    async function getReversalBilletReceipt(transactionId) {
        if (isBaasV2Enabled) {
            await getBankSlipReceiptV2(transactionId, TransactionFlow.REVERSAL_IN);
            return;
        }
        setError(true);
    }
    async function getScheduleBilletReceipt(transactionId) {
        const { data } = await triggerScheduleBillet({ id: transactionId }, false);
        if (!data?.result) {
            setError(true);
        }
        else {
            setBilletReceipt({
                flow: TransactionFlow.SCHEDULED,
                id: data.result.scheduleId,
                amount: data.result.amount,
                discounts: data.result.bankslip?.discount,
                fees: data.result.bankslip?.interest,
                fine: data.result.bankslip?.fine,
                dueDate: data.result.bankslip?.dueDate,
                transactionDate: data.result.scheduledDate,
                receiverName: data.result.beneficiaryName,
                receiverIdentificationNumber: data.result.beneficiaryDocument,
                barcode: data.result.bankslip?.barCode,
            });
        }
    }
    async function getBilletReceipt(transactionId, transactionFlow, coreBankId, instructionFlow) {
        setLoading(true);
        setError(false);
        setBilletReceipt(undefined);
        try {
            const action = {
                [TransactionFlow.CASH_OUT]: () => getPaidBilletReceipt(transactionId, coreBankId, instructionFlow ?? false),
                [TransactionFlow.CASH_IN]: () => getChargeBilletReceipt(transactionId, coreBankId),
                [TransactionFlow.SCHEDULED]: () => getScheduleBilletReceipt(transactionId),
                [TransactionFlow.REVERSAL_IN]: () => getReversalBilletReceipt(transactionId),
            }[transactionFlow];
            if (action) {
                await action();
            }
            else {
                setError(true);
            }
        }
        catch {
            setError(true);
        }
        finally {
            setLoading(false);
        }
    }
    return {
        loading,
        setLoading,
        error,
        setError,
        billetReceipt,
        setBilletReceipt,
        getBilletReceipt,
    };
}
